import { useEffect, useRef } from 'react';
import { Animated, Image, ScrollView, useWindowDimensions, type GestureResponderEvent } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { Box, Text } from '@/components/ui/primitives';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import type {
  TournamentBracketGame,
  TournamentBracketResponse,
  TournamentBracketRound,
  TournamentBracketTeamSlot,
} from '@shared/worldCupBracket';

const BRACKET_CARD_WIDTH = 280;
const BRACKET_CARD_HEIGHT = 162;
const BRACKET_BASE_GAP = 26;
const BRACKET_CONNECTOR_WIDTH = 20;
const COMPACT_BRACKET_BREAKPOINT = 820;

export type MarchMadnessBracketTeamSlot = TournamentBracketTeamSlot;
export type MarchMadnessBracketGame = TournamentBracketGame;
export type MarchMadnessBracketRound = TournamentBracketRound;
export type MarchMadnessBracketResponse = TournamentBracketResponse;

type MarchMadnessBracketViewProps = {
  bracket: MarchMadnessBracketResponse;
  compactRoundKey: string | null;
  onSelectCompactRound: (roundKey: string | null) => void;
};

type CompactBracketRound = {
  roundKey: string;
  label: string;
  games: MarchMadnessBracketGame[];
};

const formatGameTime = (value: string | Date | null) => {
  if (!value) return 'TBD';
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'TBD';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
};

const formatStatusLabel = (game: MarchMadnessBracketGame) => {
  const normalized = game.status.trim().toLowerCase();
  if (game.isFinal || normalized === 'f' || normalized === 'final') return null;
  if (
    [
      'live',
      'in_progress',
      'in-progress',
      'in progress',
      'halftime',
      'ot',
      'overtime',
      '1st-half',
      '2nd-half',
      '1st half',
      '2nd half',
      'half',
      'i',
    ].includes(normalized)
  ) {
    return 'Live';
  }
  const formattedTime = formatGameTime(game.startsAt);
  return formattedTime === 'TBD' ? null : formattedTime;
};

const getTeamDisplayName = (team: MarchMadnessBracketTeamSlot) =>
  team.shortName ?? team.abbreviation ?? team.teamName ?? 'TBD';

function LiveIndicator({ label }: { label: string }) {
  const pulseAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 0.6,
          duration: 1000,
          useNativeDriver: true,
        }),
        Animated.timing(pulseAnim, {
          toValue: 1,
          duration: 1000,
          useNativeDriver: true,
        }),
      ])
    ).start();
  }, [pulseAnim]);

  return (
    <Animated.View style={[{ opacity: pulseAnim }]}>
      <Badge variant="success">
        <Box flexDirection="row" alignItems="center" gap="xs">
          <Box width={6} height={6} backgroundColor="successForeground" />
          <Text variant="caption" fontWeight="800" color="success" fontSize={10}>
            {label.toUpperCase()}
          </Text>
        </Box>
      </Badge>
    </Animated.View>
  );
}

function BracketTeamRow({
  team,
  isGameFinal,
}: {
  team: MarchMadnessBracketTeamSlot;
  isGameFinal: boolean;
}) {
  const muted = team.isEliminated;
  const highlightedWinner = isGameFinal && team.isWinner;
  const scoreLabel = team.score !== null ? String(team.score) : '-';

  return (
    <Box style={[styles.teamRow, highlightedWinner && styles.teamRowWinner, muted && styles.teamRowMuted]}>
      <Box flexDirection="row" alignItems="center" gap="s" flex={1}>
        {team.seed !== null ? (
          <Box style={[styles.seedBadge, highlightedWinner && styles.seedBadgeWinner]}>
            <Text variant="caption" fontWeight="800" color={muted ? 'mutedForeground' : 'text'}>
              {team.seed}
            </Text>
          </Box>
        ) : null}
        {team.logoUrl ? (
          <Image source={{ uri: team.logoUrl }} style={styles.logo} resizeMode="contain" />
        ) : team.flagEmoji ? (
          <Box style={styles.flagBadge}>
            <Text style={styles.flagText}>{team.flagEmoji}</Text>
          </Box>
        ) : (
          <Box style={styles.logoPlaceholder} />
        )}
        <Text
          variant="bodySmall"
          fontWeight={team.isWinner ? '800' : '700'}
          color={muted ? 'mutedForeground' : 'text'}
          numberOfLines={1}
          style={styles.teamName}
        >
          {getTeamDisplayName(team)}
        </Text>
      </Box>
      <Box style={[styles.scorePill, highlightedWinner && styles.scorePillWinner, muted && styles.scorePillMuted]}>
        <Text
          variant="bodySmall"
          fontWeight="900"
          color={muted ? 'mutedForeground' : 'text'}
        >
          {scoreLabel}
        </Text>
      </Box>
    </Box>
  );
}

function BracketGameCard({
  game,
  showConnector,
  roundIndex,
  gameIndex,
  compact = false,
}: {
  game: MarchMadnessBracketGame;
  showConnector: boolean;
  roundIndex: number;
  gameIndex: number;
  compact?: boolean;
}) {
  const statusLabel = formatStatusLabel(game);
  const normalized = game.status.trim().toLowerCase();
  const isLive = [
    'live',
    'in_progress',
    'in-progress',
    'in progress',
    'halftime',
    'ot',
    'overtime',
    '1st-half',
    '2nd-half',
    '1st half',
    '2nd half',
    'half',
    'i',
  ].includes(normalized);
  const isTopGame = gameIndex % 2 === 0;

  return (
    <Box style={[styles.gameShell, compact && styles.gameShellCompact]}>
      {roundIndex > 0 ? <Box style={styles.connectorLeft} /> : null}
      <Card style={[styles.gameCard, game.isFinal && styles.gameCardWithWinner, isLive && styles.gameCardLive]}>
        <CardContent style={styles.gameCardContent}>
          <Box flexDirection="row" justifyContent="space-between" alignItems="center" marginBottom="s">
            {statusLabel ? (
              isLive ? (
                <LiveIndicator label={statusLabel} />
              ) : (
                <Badge variant="default">
                  <Text variant="caption" fontWeight="800" color="text">
                    {statusLabel}
                  </Text>
                </Badge>
              )
            ) : (
              <Box />
            )}
            {game.region ? (
              <Text variant="caption" color="mutedForeground" fontWeight="700" numberOfLines={1}>
                {game.region}
              </Text>
            ) : null}
          </Box>
          <Box gap="xs">
            <BracketTeamRow team={game.teams[0]} isGameFinal={game.isFinal} />
            <BracketTeamRow team={game.teams[1]} isGameFinal={game.isFinal} />
          </Box>
        </CardContent>
      </Card>
      {showConnector ? (
        <>
          <Box style={styles.connectorHorizontal} />
          <Box style={styles.connectorVertical(roundIndex, isTopGame)} />
        </>
      ) : null}
    </Box>
  );
}

export function TournamentBracketView({
  bracket,
  compactRoundKey,
  onSelectCompactRound,
}: MarchMadnessBracketViewProps) {
  const { width } = useWindowDimensions();
  const isNarrow = width < COMPACT_BRACKET_BREAKPOINT;
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const compactTranslateX = useRef(new Animated.Value(0)).current;
  const compactOpacity = useRef(new Animated.Value(1)).current;
  const previousFocusedRoundIndexRef = useRef<number | null>(null);
  const compactRounds: CompactBracketRound[] =
    bracket.firstFourGames.length > 0
      ? [{ roundKey: 'first-four', label: 'First Four', games: bracket.firstFourGames }, ...bracket.rounds]
      : bracket.rounds;
  const defaultRoundKey = bracket.rounds[0]?.roundKey ?? null;
  const focusedRoundKey = compactRoundKey ?? defaultRoundKey;
  const focusedRound = compactRounds.find((round) => round.roundKey === focusedRoundKey) ?? bracket.rounds[0] ?? null;
  const focusedRoundIndex = Math.max(
    compactRounds.findIndex((round) => round.roundKey === focusedRoundKey),
    0,
  );
  const resolvedFocusedRoundKey = focusedRound?.roundKey ?? null;
  const compactGroups = focusedRound
    ? (() => {
        const groups = new Map<string, MarchMadnessBracketGame[]>();
        for (const game of focusedRound.games) {
          const key = game.region ?? '';
          const group = groups.get(key);
          if (group) {
            group.push(game);
          } else {
            groups.set(key, [game]);
          }
        }
        return Array.from(groups.entries());
      })()
    : [];

  useEffect(() => {
    if (!isNarrow) {
      previousFocusedRoundIndexRef.current = focusedRoundIndex;
      compactTranslateX.setValue(0);
      compactOpacity.setValue(1);
      return;
    }

    const previousIndex = previousFocusedRoundIndexRef.current;
    previousFocusedRoundIndexRef.current = focusedRoundIndex;

    if (previousIndex === null || previousIndex === focusedRoundIndex) {
      compactTranslateX.setValue(0);
      compactOpacity.setValue(1);
      return;
    }

    const direction = focusedRoundIndex > previousIndex ? 1 : -1;
    compactTranslateX.setValue(direction * 28);
    compactOpacity.setValue(0.9);

    Animated.parallel([
      Animated.spring(compactTranslateX, {
        toValue: 0,
        damping: 18,
        mass: 0.8,
        stiffness: 180,
        useNativeDriver: true,
      }),
      Animated.timing(compactOpacity, {
        toValue: 1,
        duration: 180,
        useNativeDriver: true,
      }),
    ]).start();
  }, [compactOpacity, compactTranslateX, focusedRoundIndex, isNarrow]);

  const handleCompactTouchStart = (event: GestureResponderEvent) => {
    const touch = event.nativeEvent.touches[0] ?? event.nativeEvent.changedTouches[0];
    if (!touch) {
      swipeStartRef.current = null;
      return;
    }

    swipeStartRef.current = {
      x: touch.pageX,
      y: touch.pageY,
    };
  };

  const handleCompactTouchEnd = (event: GestureResponderEvent) => {
    if (!isNarrow || compactRounds.length <= 1 || !swipeStartRef.current) {
      swipeStartRef.current = null;
      return;
    }

    const touch = event.nativeEvent.changedTouches[0] ?? event.nativeEvent.touches[0];
    if (!touch) {
      swipeStartRef.current = null;
      return;
    }

    const deltaX = touch.pageX - swipeStartRef.current.x;
    const deltaY = touch.pageY - swipeStartRef.current.y;
    swipeStartRef.current = null;

    if (Math.abs(deltaX) <= Math.abs(deltaY) || Math.abs(deltaX) < 40) {
      return;
    }

    if (deltaX < 0) {
      const nextRound = compactRounds[focusedRoundIndex + 1];
      if (nextRound) {
        onSelectCompactRound(nextRound.roundKey);
      }
      return;
    }

    const previousRound = compactRounds[focusedRoundIndex - 1];
    if (previousRound) {
      onSelectCompactRound(previousRound.roundKey);
    }
  };

  return (
    <Box gap="m">
      {isNarrow && focusedRound ? (
        <Box
          onTouchStart={handleCompactTouchStart}
          onTouchEnd={handleCompactTouchEnd}
          style={styles.compactInteractionArea}
        >
          <Box gap="m">
            <Animated.View
              style={[
                styles.compactPage(width),
                {
                  opacity: compactOpacity,
                  transform: [{ translateX: compactTranslateX }],
                },
              ]}
            >
              <Box style={styles.compactRoundHeader}>
                <Text variant="caption" fontWeight="900" color="mutedForeground">
                  {focusedRound.label.toUpperCase()}
                </Text>
              </Box>
              <Box gap="m">
                {compactGroups.map(([region, games]) => (
                  <Box key={region} gap="s" style={styles.compactRegionGroup}>
                    {region ? (
                      <Text variant="caption" fontWeight="800" color="mutedForeground">
                        {region.toUpperCase()}
                      </Text>
                    ) : null}
                    {games.map((game, gameIndex) => (
                      <BracketGameCard key={game.id} game={game} showConnector={false} roundIndex={0} gameIndex={gameIndex} compact />
                    ))}
                  </Box>
                ))}
              </Box>
            </Animated.View>
          </Box>
          <Box style={styles.compactPagerIndicators}>
            {compactRounds.map((round) => (
              <Box
                key={round.roundKey}
                style={[styles.compactPagerIndicator, round.roundKey === resolvedFocusedRoundKey && styles.compactPagerIndicatorActive]}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <Box flexDirection="row" gap="m" paddingRight="l">
            {bracket.rounds.map((round, roundIndex) => (
              <Box key={round.roundKey} style={styles.roundColumn()}>
                <Box style={styles.roundHeader}>
                  <Text variant="caption" fontWeight="900" color="mutedForeground">
                    {round.label.toUpperCase()}
                  </Text>
                </Box>
                <Box style={styles.roundGames(roundIndex)}>
                  {round.games.map((game, gameIndex) => (
                    <BracketGameCard
                      key={game.id}
                      game={game}
                      showConnector={roundIndex < bracket.rounds.length - 1}
                      roundIndex={roundIndex}
                      gameIndex={gameIndex}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        </ScrollView>
      )}

      {!isNarrow && bracket.firstFourGames.length > 0 ? (
        <Box gap="s">
          <Text variant="caption" fontWeight="800" color="mutedForeground">
            FIRST FOUR
          </Text>
          <Box style={styles.compactContent}>
            {bracket.firstFourGames.map((game, gameIndex) => (
              <BracketGameCard key={game.id} game={game} showConnector={false} roundIndex={0} gameIndex={gameIndex} />
            ))}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}

export const MarchMadnessBracketView = TournamentBracketView;

const styles = StyleSheet.create((theme) => ({
  compactContent: {
    gap: theme.spacing.m,
  },
  compactInteractionArea: {
    gap: theme.spacing.m,
  },
  compactPage: (pageWidth: number) => ({
    alignItems: 'center',
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.xs,
    width: pageWidth > 0 ? pageWidth : '100%',
  }),
  compactPagerIndicator: {
    backgroundColor: theme.colors.border,
    borderRadius: 0,
    height: 8,
    width: 8,
  },
  compactPagerIndicatorActive: {
    backgroundColor: theme.colors.acid,
    width: 20,
  },
  compactPagerIndicators: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.spacing.xs,
    justifyContent: 'center',
  },
  compactRegionGroup: {
    maxWidth: BRACKET_CARD_WIDTH + theme.spacing.l,
    width: '100%',
  },
  compactRoundHeader: {
    backgroundColor: theme.colors.paperDark,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 2,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.s,
  },
  connectorHorizontal: {
    backgroundColor: theme.colors.press,
    height: 2,
    position: 'absolute',
    right: -BRACKET_CONNECTOR_WIDTH,
    top: '50%',
    width: BRACKET_CONNECTOR_WIDTH,
  },
  connectorLeft: {
    backgroundColor: theme.colors.press,
    height: 2,
    left: -theme.spacing.m,
    position: 'absolute',
    top: '50%',
    width: theme.spacing.m,
  },
  connectorVertical: (roundIndex: number, isTopGame: boolean) => {
    const distanceBetweenCenters = (BRACKET_CARD_HEIGHT + BRACKET_BASE_GAP) * Math.pow(2, roundIndex);
    const connectorHeight = distanceBetweenCenters / 2;

    return {
      backgroundColor: theme.colors.press,
      height: connectorHeight,
      position: 'absolute',
      right: -BRACKET_CONNECTOR_WIDTH,
      top: '50%',
      transform: isTopGame ? [] : [{ translateY: -connectorHeight }],
      width: 2,
    };
  },
  flagBadge: {
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 2,
    height: 24,
    justifyContent: 'center',
    width: 24,
  },
  flagText: {
    fontSize: 14,
  },
  gameCard: {
    backgroundColor: theme.colors.paper,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 3,
    minHeight: BRACKET_CARD_HEIGHT,
    shadowColor: theme.colors.press,
    shadowOffset: { width: 6, height: 6 },
    shadowOpacity: 1,
    shadowRadius: 0,
    width: BRACKET_CARD_WIDTH,
  },
  gameCardContent: {
    gap: theme.spacing.xs,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
  gameCardLive: {
    borderColor: theme.colors.success,
    borderWidth: 2,
  },
  gameCardWithWinner: {
    borderColor: theme.colors.press,
  },
  gameShell: {
    marginRight: theme.spacing.l,
    position: 'relative',
  },
  gameShellCompact: {
    alignSelf: 'center',
    marginRight: 0,
    width: BRACKET_CARD_WIDTH,
  },
  logo: {
    height: 24,
    width: 24,
  },
  logoPlaceholder: {
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 2,
    height: 24,
    width: 24,
  },
  roundColumn: () => ({
    gap: theme.spacing.m,
    minWidth: BRACKET_CARD_WIDTH + BRACKET_CONNECTOR_WIDTH,
  }),
  roundGames: (roundIndex: number) => ({
    gap:
      roundIndex === 0
        ? BRACKET_BASE_GAP
        : (BRACKET_CARD_HEIGHT + BRACKET_BASE_GAP) * Math.pow(2, roundIndex) - BRACKET_CARD_HEIGHT,
    paddingTop:
      roundIndex === 0
        ? 0
        : ((BRACKET_CARD_HEIGHT + BRACKET_BASE_GAP) * (Math.pow(2, roundIndex) - 1)) / 2,
  }),
  roundHeader: {
    backgroundColor: theme.colors.paperDark,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 2,
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.s,
  },
  scorePill: {
    alignItems: 'center',
    backgroundColor: theme.colors.paperDark,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 1,
    minWidth: 34,
    paddingHorizontal: theme.spacing.s,
    paddingVertical: 6,
  },
  scorePillMuted: {
    opacity: 0.7,
  },
  scorePillWinner: {
    backgroundColor: theme.colors.acid,
  },
  seedBadge: {
    alignItems: 'center',
    backgroundColor: theme.colors.paperDark,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 2,
    height: 26,
    justifyContent: 'center',
    width: 26,
  },
  seedBadgeWinner: {
    backgroundColor: theme.colors.acid,
    borderColor: theme.colors.press,
  },
  teamName: {
    flexShrink: 1,
  },
  teamRow: {
    alignItems: 'center',
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.press,
    borderRadius: 0,
    borderWidth: 2,
    flexDirection: 'row',
    gap: theme.spacing.s,
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.s,
    paddingVertical: theme.spacing.s,
  },
  teamRowMuted: {
    backgroundColor: theme.colors.paperDark,
    opacity: 0.6,
  },
  teamRowWinner: {
    backgroundColor: theme.colors.acid,
    borderColor: theme.colors.press,
    borderWidth: 2,
    shadowColor: theme.colors.press,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
}));
